import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Paper,
  TextField,
  MenuItem,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
  TablePagination,
  IconButton,
  Alert,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp, Search } from "@mui/icons-material";

import authService from "../Auth/AuthService";
import AttendanceCalendar from "./AttendanceCalendar";
import AttendanceTable from "./AttendanceTable";
import LeaveForm from "./LeaveForm";
import { getDistance } from "../utils/distance";

const getShift = (time) => {
  const h = time.hour();
  if (h >= 6 && h < 14) return "MORNING";
  if (h >= 14 && h < 22) return "EVENING";
  return "NIGHT";
};

const MONTHS = [
  "JANUARY", "FEBRUARY", "MARCH", "APRIL",
  "MAY", "JUNE", "JULY", "AUGUST",
  "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"
];

const info = JSON.parse(localStorage.getItem("device")) || null;

export default function AttendanceView({ empId, onSuccess, user }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const hasFetched = useRef(false);

  const [attendance, setAttendance] = useState([]);
  const [selected, setSelected] = useState(null);

  const [shift, setShift] = useState("");
  const [inTime, setInTime] = useState("");
  const [punchedIn, setPunchedIn] = useState(false);
  const [existing, setExisting] = useState(null);
  const [month, setMonth] = useState("")

  const [viewAll, setViewAll] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [openForm, setOpenForm] = useState(false);
  const [distance, setDistance] = useState(null);
  const [school, setSchool] = useState({});

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    const m = dayjs().format("MMMM").toUpperCase();
    setMonth(m);
    loadData();
    loadSchoolData();
  }, []);

  const loadData = async () => {
    const now = dayjs();
    const m = now.format("MMMM").toUpperCase();
    const year = now.year();

    try {
      const url = authService.pathUrl("GET", "/attendance/calendar");
      const res = await authService.getAuth(
        `${url}?empId=${empId}&month=${month ? month : m}&year=${year}`
      );

      const today = res?.find(
        d =>
          dayjs(d.attendDate).isSame(now, "day") &&
          d.inTime &&
          !d.outTime
      );

      if (today) {
        setExisting(today);
        setShift(today.shift);
        setInTime(today.inTime);
        setPunchedIn(true);
      }

      setAttendance(Array.isArray(res) ? res : []);
    } catch {
      setAttendance([]);
    }
  };

  const loadSchoolData = async () => {

    if (user?.roles === "TEACHER") {
    try {
      const url = authService.pathUrl("GET", "/schools/");
      const res = await authService.getAuth(url + user.mas_id);
      setSchool(res);
      handleCalculate(info, res?.addressDTO);
    } catch {
      setSchool({});
    }
  }
  };

  const handleCalculate = (emp, sch) => {

    if (!info) {
      <Alert>{
        "This app requires access to your location. Please enable location permission to proceed."
      }</Alert>

      return false;
    }
    const { km, meters } = getDistance(info.latitude, info.longitude, sch.latitude, sch.longitude,);
    setDistance({ km, meters });
  };

  const punchIn = async () => {
    const now = dayjs();
    const autoShift = getShift(now);

    const displayText = distance
      ? distance.meters < 1000
        ? `${distance.meters.toFixed(0)} m`
        : `${distance.km.toFixed(2)} km`
      : null;

    const payload = {
      empId,
      attendDate: now,
      inTime: now.format("HH:mm"),
      outTime: "",
      shift: autoShift,
      month: now.format("MMMM").toUpperCase(),
      inDistance: displayText,
    };

    setShift(autoShift);
    setInTime(payload.inTime);
    setPunchedIn(true);

    await authService.postAuth(
      authService.pathUrl("POST", "/attendance/punch-in"),
      payload
    );
    loadData();
  };

  const punchOut = async () => {
    if (!existing) return;

    const displayText = distance
      ? distance.meters < 1000
        ? `${distance.meters.toFixed(0)} m`
        : `${distance.km.toFixed(2)} km`
      : null;

    await authService.putAuth(
      authService.pathUrl("PUT", "/attendance/punch-out"),
      {
        ...existing,
        outTime: dayjs().format("HH:mm"),
        outDistance: displayText,
      }
    );

    setPunchedIn(false);
    loadData();
  };

  const paginated = attendance.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "none",
        overflowX: "hidden",
        p: 1,
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'column',
        alignContent: 'space-around'
      }}
    >
      {/* ---------- PUNCH SECTION ---------- */}
      <Box sx={{ p: 1.5, mt: 2, borderRadius: 2 }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">

            <TextField
              select
              size="small"
              label="Shift"
              value={shift}
              disabled
              fullWidth
            >
              {["MORNING", "EVENING", "NIGHT"].map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label="In Time"
              value={inTime || "--:--"}
              disabled
              fullWidth
            />
            <Button
              fullWidth
              title="Punch In"
              variant="contained"
              disabled={punchedIn}
              onClick={punchIn}
              sx={{ bgcolor: '#332c1b', py: 0.8, borderRadius: 2 }}
            >
              In
            </Button>
            <Button
              fullWidth
              title="Punch Out"
              variant="contained"
              disabled={!punchedIn}
              onClick={punchOut}
              sx={{ bgcolor: '#332c1b', py: 0.8, borderRadius: 2 }}
            >
              Out
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              select
              size="small"
              label="Month"
              value={month}
              sx={{ width: "50%" }}
              onChange={(e) => setMonth(e.target.value)}
            >
              {MONTHS.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </TextField>

            <IconButton
              color="warning"
              variant="contained"
              onClick={() => { loadData() }}
            >
              <Search sx={{ border: "2px solid", fontWeight: "bold", borderRadius: "4px" }} />
            </IconButton>

            <IconButton sx={{ border: "1px solid", borderRadius: "10px", my: 1 }}
              title="View all"
              color="#332c1b"
              size="small"
              onClick={() => {
                setViewAll(v => !v);
                setPage(0);
              }}
            // endIcon={viewAll ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            >
              View All  {viewAll ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>

            <IconButton sx={{ border: "1px solid", borderRadius: "10px", my: 1 }}
              color="#332c1b"
              size="small"
              onClick={() => setOpenForm(!openForm)}
              title="Add Leave"
            >
              Add Leave  {openForm ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>

          </Stack>
        </Stack>

        {openForm && (
          <LeaveForm empId={empId}
            onSuccess={() => {
              loadData();
              setOpenForm(false);
            }
            } />
        )}

      </Box>

      {/* ---------- TABLE ---------- */}
      {viewAll | isMobile && (
        <Paper sx={{ mt: 2 }}>
          <AttendanceTable records={paginated} />
          <TablePagination
            component="div"
            count={attendance.length}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => {
              setRowsPerPage(+e.target.value);
              setPage(0);
            }}
          />
        </Paper>
      )}

      {/* ---------- CALENDAR ---------- */}
      {!isMobile && (
        <Box mt={5}>
          <AttendanceCalendar
            data={attendance}
            onSelect={day => {
              if (!day) return;
              const rec = attendance.find(d =>
                dayjs(d.attendDate).isSame(day, "day")
              );
              setSelected(rec ? [rec] : null);
            }}
          />
        </Box>
      )}

      {/* ---------- SELECTED DAY ---------- */}
      {!isMobile && selected && (
        <Box mt={2}>
          <AttendanceTable records={selected}
            onSuccess={() => {
              loadData();
            }
            }
          />
        </Box>
      )}

      {/* ---------- LEGEND ---------- */}
      <Box mt={2} fontSize={12} textAlign="center">
        🟢 PRESENT &nbsp; 🟡 PENDING &nbsp; 🟠 LATE &nbsp;
        🔵 LEAVE &nbsp; 🔴 HOLIDAY &nbsp; ⚪ ABSENT
      </Box>
    </Box>
  );
}
